import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Briefcase, 
  Clock, 
  CheckCircle2, 
  CheckCheck, 
  XCircle, 
  AlertTriangle, 
  Archive 
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface VendorJobCountsByStatus {
  awarded: number;
  in_progress: number;
  completed_pending_review: number;
  accepted: number;
  rejected: number;
  disputed: number;
  closed: number;
}

interface VendorJobStatusTableProps {
  data: VendorJobCountsByStatus;
  loading: boolean;
}

const VendorJobStatusTable = ({ data, loading }: VendorJobStatusTableProps) => {
  const navigate = useNavigate();

  const statusConfig = [
    {
      status: "awarded",
      label: "Awarded",
      icon: Briefcase,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      status: "in_progress",
      label: "In Progress",
      icon: Clock,
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
    },
    {
      status: "completed_pending_review",
      label: "Pending Review",
      icon: CheckCircle2,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      status: "accepted",
      label: "Accepted",
      icon: CheckCheck,
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      status: "rejected",
      label: "Rejected",
      icon: XCircle,
      color: "text-red-600",
      bgColor: "bg-red-50",
    },
    {
      status: "disputed",
      label: "Disputed",
      icon: AlertTriangle,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      status: "closed",
      label: "Closed",
      icon: Archive,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
    },
  ];

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center justify-between py-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-5 w-8" />
          </div>
        ))}
      </div>
    );
  }

  const totalJobs = Object.values(data).reduce((sum, count) => sum + count, 0);

  if (totalJobs === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No jobs yet. Start bidding on RFQs to see your job status here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {statusConfig.map(({ status, label, icon: Icon, color, bgColor }) => {
        const count = data[status as keyof VendorJobCountsByStatus];

        return (
          <button
            key={status}
            onClick={() => navigate(`/jobs?status=${status}`)}
            className="w-full flex items-center justify-between py-2 px-2.5 rounded-md hover:bg-accent transition-colors text-left group"
          >
            <div className="flex items-center space-x-2.5">
              <div className={`p-1.5 rounded-md ${bgColor}`}>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
              <span className="font-medium text-sm">{label}</span>
            </div>
            <Badge variant="secondary" className="font-semibold">
              {count}
            </Badge>
          </button>
        );
      })}
      
      <div className="pt-2 mt-2 border-t flex items-center justify-between px-2.5">
        <span className="font-semibold text-sm">Total Jobs</span>
        <Badge className="font-semibold">{totalJobs}</Badge>
      </div>
    </div>
  );
};

export default VendorJobStatusTable;
